import { useCallback, useEffect, useMemo, useState } from "react";

import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import {
	type AdminReviewItem,
	type AdminReviewStatus,
	type AdminReviewsStats,
	reviewsApi,
} from "@/lib/catalog/reviews-api";

function StarRating({ rating, size = 15 }: { rating: number; size?: number }) {
	return (
		<div
			className="inline-flex items-center gap-0.5"
			title={`${rating}/5 étoiles`}
		>
			{[1, 2, 3, 4, 5].map((s) => (
				<svg
					key={s}
					style={{ width: size, height: size }}
					className={s <= rating ? "fill-amber-400 text-amber-400" : "fill-amber-100 text-amber-200"}
					viewBox="0 0 24 24"
				>
					<title>{`${rating}/5 étoiles`}</title>
					<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
				</svg>
			))}
		</div>
	);
}

function formatDate(dateStr: string) {
	try {
		const d = new Date(dateStr);
		const dateFormatted = d.toLocaleDateString("fr-FR", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});
		const timeFormatted = d.toLocaleTimeString("fr-FR", {
			hour: "2-digit",
			minute: "2-digit",
		});
		return { dateFormatted, timeFormatted };
	} catch {
		return { dateFormatted: dateStr, timeFormatted: "" };
	}
}

interface RowMenu {
	id: string;
	top: number;
	left: number;
}

const PAGE_SIZE = 15;

export function AdminProductReviewsPage() {
	const toast = useToast();
	const confirm = useConfirm();

	const [reviews, setReviews] = useState<AdminReviewItem[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<AdminReviewStatus | "">("");
	const [search, setSearch] = useState("");
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [menu, setMenu] = useState<RowMenu | null>(null);

	const [stats, setStats] = useState<AdminReviewsStats>({
		pending: 0,
		approved: 0,
		rejected: 0,
		total: 0,
	});

	// Modales & Drawer
	const [activeReview, setActiveReview] = useState<AdminReviewItem | null>(null);
	const [rejectingId, setRejectingId] = useState<string | null>(null);
	const [rejectionReason, setRejectionReason] = useState("");
	const [deletingReview, setDeletingReview] = useState<AdminReviewItem | null>(null);
	const [submittingAction, setSubmittingAction] = useState(false);

	const totalPages = useMemo(
		() => Math.max(1, Math.ceil(total / PAGE_SIZE)),
		[total],
	);
	const from = reviews.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
	const to = Math.min(page * PAGE_SIZE, total);

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const res = await reviewsApi.list({
				page,
				pageSize: PAGE_SIZE,
				status: statusFilter || undefined,
				search: search.trim() || undefined,
			});
			setReviews(res.data);
			setTotal(res.total);
			setStats(res.stats);

			setActiveReview((prev) => {
				if (!prev) return null;
				return res.data.find((item) => item.id === prev.id) || prev;
			});
		} catch {
			toast.error("Erreur lors du chargement des avis produits.");
		} finally {
			setLoading(false);
		}
	}, [page, statusFilter, search, toast]);

	useEffect(() => {
		const timer = setTimeout(() => {
			loadData();
		}, 300);
		return () => clearTimeout(timer);
	}, [loadData]);

	// Écouteur de touche Échap pour fermer les éléments d'UI
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				if (rejectingId) {
					setRejectingId(null);
					setRejectionReason("");
				} else if (deletingReview) {
					setDeletingReview(null);
				} else if (activeReview) {
					setActiveReview(null);
				} else if (menu) {
					setMenu(null);
				}
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [rejectingId, deletingReview, activeReview, menu]);

	const toggleSelect = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const openMenu = (e: React.MouseEvent, id: string) => {
		e.stopPropagation();
		const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
		setMenu({ id, top: r.bottom + 4, left: Math.max(8, r.right - 190) });
	};
	const closeMenu = () => setMenu(null);

	const handleApprove = async (id: string) => {
		try {
			setSubmittingAction(true);
			await reviewsApi.approve(id);
			toast.success("Avis approuvé et publié avec succès.");
			await loadData();
		} catch {
			toast.error("Erreur lors de l'approbation de l'avis.");
		} finally {
			setSubmittingAction(false);
		}
	};

	const handleConfirmReject = async () => {
		if (!rejectingId) return;
		try {
			setSubmittingAction(true);
			await reviewsApi.reject(rejectingId, rejectionReason);
			toast.success("Avis rejeté.");
			setRejectingId(null);
			setRejectionReason("");
			await loadData();
		} catch {
			toast.error("Erreur lors du rejet de l'avis.");
		} finally {
			setSubmittingAction(false);
		}
	};

	const handleConfirmDelete = async () => {
		if (!deletingReview) return;
		try {
			setSubmittingAction(true);
			await reviewsApi.delete(deletingReview.id);
			toast.success("Avis supprimé définitivement.");
			if (activeReview?.id === deletingReview.id) {
				setActiveReview(null);
			}
			setDeletingReview(null);
			await loadData();
		} catch {
			toast.error("Erreur lors de la suppression de l'avis.");
		} finally {
			setSubmittingAction(false);
		}
	};

	// Actions groupées (Bulk)
	const handleBulkApprove = async () => {
		const ids = [...selected];
		if (ids.length === 0) return;
		try {
			setSubmittingAction(true);
			await Promise.all(ids.map((id) => reviewsApi.approve(id)));
			toast.success(`${ids.length} avis approuvé(s).`);
			setSelected(new Set());
			await loadData();
		} catch {
			toast.error("Erreur lors de l'approbation groupée.");
		} finally {
			setSubmittingAction(false);
		}
	};

	const handleBulkRejectPrompt = async () => {
		const ids = [...selected];
		if (ids.length === 0) return;
		const reason = prompt(
			`Raison du rejet pour les ${ids.length} avis sélectionnés (optionnel) :`,
		);
		if (reason === null) return;
		try {
			setSubmittingAction(true);
			await Promise.all(ids.map((id) => reviewsApi.reject(id, reason)));
			toast.success(`${ids.length} avis rejeté(s).`);
			setSelected(new Set());
			await loadData();
		} catch {
			toast.error("Erreur lors du rejet groupé.");
		} finally {
			setSubmittingAction(false);
		}
	};

	const handleBulkDelete = async () => {
		const ids = [...selected];
		if (ids.length === 0) return;
		const ok = await confirm({
			title: `Supprimer ${ids.length} avis client(s)`,
			message: (
				<>
					Cette action est irréversible. Confirmez la suppression définitive des avis sélectionnés.
				</>
			),
			confirmLabel: "Supprimer",
			tone: "danger",
		});
		if (!ok) return;
		try {
			setSubmittingAction(true);
			await Promise.all(ids.map((id) => reviewsApi.delete(id)));
			toast.success(`${ids.length} avis supprimé(s).`);
			setSelected(new Set());
			await loadData();
		} catch {
			toast.error("Erreur lors de la suppression groupée.");
		} finally {
			setSubmittingAction(false);
		}
	};

	const menuItemReview = menu ? reviews.find((r) => r.id === menu.id) : null;

	return (
		<>
			{/* En-tête principal aux normes Akwaba */}
			<PageHead
				title="Avis produits"
				sub={`${total} avis client(s) au total · Modération et publication boutique`}
				actions={
					<AdminButton variant="ghost" onClick={loadData}>
						<AdminIcon n="refresh" s={14} />
						Actualiser
					</AdminButton>
				}
			/>

			{/* Cartes KPI standard avec typographie chiffres fixes */}
			<div
				className="kpi-grid"
				style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
			>
				<div className="kpi">
					<div className="kpi-l">TOTAL AVIS</div>
					<div className="kpi-v tn font-mono">{stats.total}</div>
					<div className="kpi-foot muted">historique complet</div>
				</div>
				<div className="kpi warning">
					<div className="kpi-l">EN ATTENTE</div>
					<div className="kpi-v tn font-mono">{stats.pending}</div>
					<div className="kpi-foot muted">à modérer</div>
				</div>
				<div className="kpi success">
					<div className="kpi-l">AVIS PUBLIÉS</div>
					<div className="kpi-v tn font-mono">{stats.approved}</div>
					<div className="kpi-foot muted">en ligne boutique</div>
				</div>
				<div className="kpi accent">
					<div className="kpi-l">AVIS REJETÉS</div>
					<div className="kpi-v tn font-mono">{stats.rejected}</div>
					<div className="kpi-foot muted">refusés</div>
				</div>
			</div>

			{/* Bloc principal Tableau & Barre d'actions */}
			<Card>
				<div className="toolbar">
					<div
						className="filter-bar"
						style={{ marginBottom: 0, flexWrap: "wrap" }}
					>
						<ChipFilter
							on={statusFilter === ""}
							count={stats.total}
							onClick={() => {
								setStatusFilter("");
								setPage(1);
								setSelected(new Set());
							}}
							data-testid="tab-all"
						>
							Tous
						</ChipFilter>
						<ChipFilter
							on={statusFilter === "PENDING"}
							count={stats.pending}
							onClick={() => {
								setStatusFilter("PENDING");
								setPage(1);
								setSelected(new Set());
							}}
							data-testid="tab-pending"
						>
							En attente
						</ChipFilter>
						<ChipFilter
							on={statusFilter === "APPROVED"}
							count={stats.approved}
							onClick={() => {
								setStatusFilter("APPROVED");
								setPage(1);
								setSelected(new Set());
							}}
							data-testid="tab-approved"
						>
							Approuvés
						</ChipFilter>
						<ChipFilter
							on={statusFilter === "REJECTED"}
							count={stats.rejected}
							onClick={() => {
								setStatusFilter("REJECTED");
								setPage(1);
								setSelected(new Set());
							}}
							data-testid="tab-rejected"
						>
							Rejetés
						</ChipFilter>
					</div>

					<div
						style={{
							marginLeft: "auto",
							display: "flex",
							gap: 8,
							alignItems: "center",
						}}
					>
						<div className="relative">
							<input
								type="search"
								placeholder="Rechercher produit, client, texte…"
								value={search}
								onChange={(e) => {
									setSearch(e.target.value);
									setPage(1);
								}}
								data-testid="reviews-search"
								className="focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all duration-150"
								style={{
									padding: "6px 12px",
									border: "1px solid var(--a-border)",
									borderRadius: 6,
									minWidth: 240,
									fontSize: 13,
									background: "var(--a-surface)",
									color: "var(--a-ink)",
								}}
							/>
						</div>

						{selected.size > 0 && (
							<div className="flex items-center gap-1.5 animate-in fade-in duration-150">
								<AdminButton
									variant="ghost"
									onClick={handleBulkApprove}
									disabled={submittingAction}
								>
									<AdminIcon n="check" s={14} />
									Approuver ({selected.size})
								</AdminButton>
								<AdminButton
									variant="ghost"
									onClick={handleBulkRejectPrompt}
									disabled={submittingAction}
								>
									<AdminIcon n="close" s={14} />
									Rejeter ({selected.size})
								</AdminButton>
								<AdminButton
									variant="ghost"
									onClick={handleBulkDelete}
									disabled={submittingAction}
									style={{ color: "var(--a-danger)" }}
								>
									<AdminIcon n="trash" s={14} />
									Supprimer ({selected.size})
								</AdminButton>
							</div>
						)}
					</div>
				</div>

				<div className="table-wrap" style={{ margin: 0, overflowX: "auto" }}>
					{loading ? (
						<div style={{ padding: 48, textAlign: "center" }} className="muted flex flex-col items-center justify-center gap-2">
							<div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
							<span style={{ fontSize: 13 }}>Chargement des avis client…</span>
						</div>
					) : reviews.length === 0 ? (
						<div
							style={{ padding: 48, textAlign: "center" }}
							className="muted"
							data-testid="reviews-empty"
						>
							Aucun avis trouvé pour ces filtres.
						</div>
					) : (
						<table className="table">
							<thead>
								<tr>
									<th style={{ width: 28 }}>
										<input
											type="checkbox"
											checked={
												selected.size === reviews.length && reviews.length > 0
											}
											onChange={(e) =>
												setSelected(
													e.target.checked
														? new Set(reviews.map((r) => r.id))
														: new Set(),
												)
											}
											className="cursor-pointer rounded"
										/>
									</th>
									<th style={{ minWidth: 100 }}>Date</th>
									<th style={{ minWidth: 180 }}>Produit</th>
									<th style={{ minWidth: 180 }}>Client</th>
									<th style={{ minWidth: 120 }}>Note</th>
									<th>Commentaire</th>
									<th style={{ width: 100 }}>Statut</th>
									<th style={{ width: 44 }}></th>
								</tr>
							</thead>
							<tbody>
								{reviews.map((r) => {
									const { dateFormatted, timeFormatted } = formatDate(
										r.createdAt,
									);
									const customerName = r.customer
										? `${r.customer.firstName} ${r.customer.lastName || ""}`.trim()
										: "Client anonyme";

									const isRowSelected = selected.has(r.id);

									return (
										<tr
											key={r.id}
											style={{
												cursor: "pointer",
												background: isRowSelected ? "var(--a-surface-muted)" : undefined,
											}}
											className="hover:bg-[#f7f1e8]/60 transition-colors duration-150"
											onClick={() => setActiveReview(r)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") setActiveReview(r);
											}}
											tabIndex={0}
											data-testid={`review-row-${r.id}`}
										>
											<td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
												<input
													type="checkbox"
													checked={isRowSelected}
													onChange={() => toggleSelect(r.id)}
													className="cursor-pointer rounded"
												/>
											</td>
											<td>
												<div style={{ fontWeight: 500 }} className="tabular-nums">{dateFormatted}</div>
												<div className="muted font-mono tabular-nums" style={{ fontSize: 11 }}>
													{timeFormatted}
												</div>
											</td>
											<td>
												<div style={{ fontWeight: 600, color: "var(--a-ink)" }}>
													{r.product?.title ?? "Produit inconnu"}
												</div>
												{r.product?.sku && (
													<div className="muted font-mono tabular-nums" style={{ fontSize: 11 }}>
														SKU: {r.product.sku}
													</div>
												)}
											</td>
											<td>
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: 10,
													}}
												>
													<AdminAvatar name={customerName} />
													<div>
														<div style={{ fontWeight: 600, fontSize: 13 }}>{customerName}</div>
														<div className="muted" style={{ fontSize: 11 }}>
															{r.customer?.email ?? "—"}
														</div>
													</div>
												</div>
											</td>
											<td>
												<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
													<StarRating rating={r.rating} />
													<span className="tn font-mono muted" style={{ fontWeight: 600, fontSize: 12 }}>
														{r.rating}/5
													</span>
												</div>
												{r.isVerifiedPurchase && (
													<div style={{ marginTop: 3 }}>
														<Tag variant="success" noDot>
															✓ Achat vérifié
														</Tag>
													</div>
												)}
											</td>
											<td style={{ maxWidth: 300 }}>
												{r.title && (
													<div
														style={{
															fontWeight: 600,
															fontSize: 12.5,
															marginBottom: 2,
														}}
														className="ink2"
													>
														{r.title}
													</div>
												)}
												<div
													style={{
														fontSize: 12,
														display: "-webkit-box",
														WebkitLineClamp: 2,
														WebkitBoxOrient: "vertical",
														overflow: "hidden",
														lineHeight: "1.4",
													}}
													className="muted"
												>
													{r.comment || (
														<em className="muted opacity-75">Pas de commentaire écrit</em>
													)}
												</div>
												{r.rejectionReason && (
													<div style={{ marginTop: 4 }}>
														<Tag variant="danger" noDot>
															Motif: {r.rejectionReason}
														</Tag>
													</div>
												)}
											</td>
											<td>
												{r.status === "APPROVED" && (
													<Tag variant="success">Publié</Tag>
												)}
												{r.status === "PENDING" && (
													<Tag variant="warning">En attente</Tag>
												)}
												{r.status === "REJECTED" && (
													<Tag variant="danger">Rejeté</Tag>
												)}
											</td>
											<td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="right">
												<button
													type="button"
													className="btn btn-ghost btn-icon active:scale-[0.96] transition-transform"
													aria-label="Actions"
													onClick={(e) => openMenu(e, r.id)}
													style={{ padding: 4, borderRadius: 6 }}
												>
													<AdminIcon n="dots" s={16} />
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					)}
				</div>

				{reviews.length > 0 && (
					<Pagination
						from={from}
						to={to}
						total={total}
						current={page}
						totalPages={totalPages}
						onChange={setPage}
					/>
				)}
			</Card>

			{/* Popover Menu des Actions par ligne */}
			{menu && (
				<>
					<div
						role="presentation"
						onClick={closeMenu}
						onKeyDown={closeMenu}
						style={{ position: "fixed", inset: 0, zIndex: 40 }}
					/>
					<div
						role="menu"
						className="animate-in fade-in zoom-in-95 duration-100"
						style={{
							position: "fixed",
							top: menu.top,
							left: menu.left,
							width: 190,
							background: "var(--a-surface)",
							border: "1px solid var(--a-border)",
							borderRadius: 8,
							boxShadow: "0 8px 24px -6px rgba(20, 19, 15, 0.16)",
							zIndex: 41,
							padding: 4,
						}}
					>
						<button
							type="button"
							className="hover:bg-[#f2eee3] active:scale-[0.98] transition-all"
							onClick={() => {
								closeMenu();
								const target = reviews.find((r) => r.id === menu.id);
								if (target) setActiveReview(target);
							}}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								width: "100%",
								padding: "8px 12px",
								fontSize: 12.5,
								borderRadius: 6,
								color: "var(--a-ink)",
								textAlign: "left",
								background: "none",
								border: "none",
								cursor: "pointer",
							}}
						>
							<AdminIcon n="eye" s={14} />
							Voir le détail
						</button>
						{menuItemReview?.status !== "APPROVED" && (
							<button
								type="button"
								className="hover:bg-[#f2eee3] active:scale-[0.98] transition-all"
								onClick={() => {
									closeMenu();
									handleApprove(menu.id);
								}}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									width: "100%",
									padding: "8px 12px",
									fontSize: 12.5,
									borderRadius: 6,
									color: "var(--a-ink)",
									textAlign: "left",
									background: "none",
									border: "none",
									cursor: "pointer",
								}}
							>
								<AdminIcon n="check" s={14} />
								Approuver l'avis
							</button>
						)}
						{menuItemReview?.status !== "REJECTED" && (
							<button
								type="button"
								className="hover:bg-[#f2eee3] active:scale-[0.98] transition-all"
								onClick={() => {
									closeMenu();
									setRejectingId(menu.id);
								}}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									width: "100%",
									padding: "8px 12px",
									fontSize: 12.5,
									borderRadius: 6,
									color: "var(--a-ink)",
									textAlign: "left",
									background: "none",
									border: "none",
									cursor: "pointer",
								}}
							>
								<AdminIcon n="close" s={14} />
								Rejeter l'avis
							</button>
						)}
						<button
							type="button"
							className="hover:bg-[#f8d9d2]/40 active:scale-[0.98] transition-all"
							onClick={() => {
								closeMenu();
								const target = reviews.find((r) => r.id === menu.id);
								if (target) setDeletingReview(target);
							}}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								width: "100%",
								padding: "8px 12px",
								fontSize: 12.5,
								borderRadius: 6,
								color: "var(--a-danger)",
								textAlign: "left",
								background: "none",
								border: "none",
								cursor: "pointer",
							}}
						>
							<AdminIcon n="trash" s={14} />
							Supprimer
						</button>
					</div>
				</>
			)}

			{/* PANNEAU LATÉRAL DRAWER (DÉTAILS COMPLETS AVEC ANIMATION) */}
			{activeReview && (
				<>
					<div
						role="presentation"
						className="animate-in fade-in duration-200"
						onClick={() => setActiveReview(null)}
						onKeyDown={(e) => {
							if (e.key === "Escape" || e.key === "Enter") setActiveReview(null);
						}}
						style={{
							position: "fixed",
							inset: 0,
							background: "rgba(20, 19, 15, 0.4)",
							backdropFilter: "blur(3px)",
							zIndex: 9990,
						}}
					/>
					<div
						className="animate-in slide-in-from-right duration-200"
						style={{
							position: "fixed",
							top: 0,
							right: 0,
							bottom: 0,
							width: 480,
							maxWidth: "100vw",
							background: "var(--a-surface)",
							borderLeft: "1px solid var(--a-border)",
							boxShadow: "-10px 0 30px rgba(20, 19, 15, 0.15)",
							zIndex: 9999,
							display: "flex",
							flexDirection: "column",
						}}
					>
						{/* En-tête Drawer */}
						<div
							style={{
								padding: "16px 20px",
								borderBottom: "1px solid var(--a-border)",
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								background: "var(--a-surface-muted)",
							}}
						>
							<div>
								<h3
									style={{
										margin: 0,
										fontSize: 16,
										fontWeight: 700,
										color: "var(--a-ink)",
									}}
								>
									Détail de l'avis client
								</h3>
								<div style={{ fontSize: 12, color: "var(--a-ink-soft)", marginTop: 2 }} className="tabular-nums">
									Soumis le{" "}
									{new Date(activeReview.createdAt).toLocaleDateString("fr-FR", {
										day: "2-digit",
										month: "long",
										year: "numeric",
										hour: "2-digit",
										minute: "2-digit",
									})}
								</div>
							</div>
							<AdminButton
								variant="ghost"
								iconOnly
								onClick={() => setActiveReview(null)}
								title="Fermer (Échap)"
							>
								<AdminIcon n="close" s={16} />
							</AdminButton>
						</div>

						{/* Corps Drawer avec Rayons Concentriques */}
						<div style={{ flex: 1, overflowY: "auto", padding: 20 }} className="space-y-4">
							{/* Note & Statut */}
							<div
								style={{
									padding: 16,
									borderRadius: 10,
									background: "var(--a-surface-muted)",
									border: "1px solid var(--a-border)",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div>
									<div
										style={{
											fontSize: 11,
											fontWeight: 700,
											color: "var(--a-ink-soft)",
											textTransform: "uppercase",
											letterSpacing: "0.05em",
										}}
									>
										Évaluation globale
									</div>
									<div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
										<StarRating rating={activeReview.rating} size={20} />
										<strong style={{ fontSize: 16, color: "var(--a-ink)" }} className="font-mono tabular-nums">
											{activeReview.rating} / 5
										</strong>
									</div>
								</div>
								<div>
									{activeReview.status === "APPROVED" && <Tag variant="success">Publié</Tag>}
									{activeReview.status === "PENDING" && <Tag variant="warning">En attente</Tag>}
									{activeReview.status === "REJECTED" && <Tag variant="danger">Rejeté</Tag>}
								</div>
							</div>

							{/* Client */}
							<div
								style={{
									padding: 16,
									borderRadius: 10,
									border: "1px solid var(--a-border)",
									background: "var(--a-surface)",
								}}
							>
								<div
									style={{
										fontSize: 11,
										fontWeight: 700,
										color: "var(--a-ink-soft)",
										textTransform: "uppercase",
										letterSpacing: "0.05em",
										marginBottom: 10,
									}}
								>
									Auteur de l'avis
								</div>
								<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
									<AdminAvatar
										name={
											activeReview.customer
												? `${activeReview.customer.firstName} ${activeReview.customer.lastName || ""}`
												: "Client anonyme"
										}
										size="lg"
									/>
									<div>
										<div style={{ fontWeight: 700, fontSize: 14, color: "var(--a-ink)" }}>
											{activeReview.customer
												? `${activeReview.customer.firstName} ${activeReview.customer.lastName || ""}`
												: "Client anonyme"}
										</div>
										<div style={{ fontSize: 12, color: "var(--a-ink-soft)" }}>
											{activeReview.customer?.email ?? "—"}
										</div>
										{activeReview.isVerifiedPurchase && (
											<div style={{ marginTop: 6 }}>
												<Tag variant="success" noDot>
													✓ Achat vérifié
												</Tag>
											</div>
										)}
									</div>
								</div>
							</div>

							{/* Produit */}
							<div
								style={{
									padding: 16,
									borderRadius: 10,
									border: "1px solid var(--a-border)",
									background: "var(--a-surface)",
								}}
							>
								<div
									style={{
										fontSize: 11,
										fontWeight: 700,
										color: "var(--a-ink-soft)",
										textTransform: "uppercase",
										letterSpacing: "0.05em",
										marginBottom: 4,
									}}
								>
									Produit concerné
								</div>
								<div style={{ fontWeight: 700, fontSize: 14, color: "var(--a-ink)" }}>
									{activeReview.product?.title || "Produit inconnu"}
								</div>
								{activeReview.product?.sku && (
									<div className="font-mono muted tabular-nums" style={{ fontSize: 11, marginTop: 3 }}>
										SKU: {activeReview.product.sku}
									</div>
								)}
							</div>

							{/* Commentaire */}
							<div
								style={{
									padding: 16,
									borderRadius: 10,
									border: "1px solid var(--a-border)",
									background: "var(--a-surface)",
								}}
							>
								<div
									style={{
										fontSize: 11,
										fontWeight: 700,
										color: "var(--a-ink-soft)",
										textTransform: "uppercase",
										letterSpacing: "0.05em",
										marginBottom: 10,
									}}
								>
									Commentaire rédigé
								</div>
								{activeReview.title && (
									<h4
										style={{
											margin: "0 0 8px",
											fontSize: 14,
											fontWeight: 700,
											color: "var(--a-ink)",
										}}
									>
										{activeReview.title}
									</h4>
								)}
								<p
									style={{
										margin: 0,
										fontSize: 13,
										lineHeight: 1.6,
										color: "var(--a-ink-2)",
										whiteSpace: "pre-line",
									}}
								>
									{activeReview.comment || (
										<em className="muted opacity-75">Aucun texte de commentaire</em>
									)}
								</p>
							</div>

							{/* Motif de rejet si présent */}
							{activeReview.rejectionReason && (
								<div
									style={{
										padding: 14,
										borderRadius: 8,
										background: "var(--a-danger-soft)",
										border: "1px solid var(--a-danger)",
										display: "flex",
										gap: 10,
										alignItems: "flex-start",
									}}
								>
									<div style={{ color: "var(--a-danger)", marginTop: 2 }}>
										<AdminIcon n="alert" s={16} />
									</div>
									<div>
										<div
											style={{
												fontSize: 11,
												fontWeight: 700,
												color: "var(--a-danger)",
												textTransform: "uppercase",
												letterSpacing: "0.04em",
											}}
										>
											Raison du rejet
										</div>
										<div style={{ fontSize: 12.5, color: "var(--a-ink)", marginTop: 2 }}>
											{activeReview.rejectionReason}
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Pied du Drawer */}
						<div
							style={{
								padding: "14px 20px",
								borderTop: "1px solid var(--a-border)",
								background: "var(--a-surface-muted)",
								display: "flex",
								alignItems: "center",
								gap: 10,
							}}
						>
							{activeReview.status !== "APPROVED" && (
								<AdminButton
									variant="primary"
									onClick={() => handleApprove(activeReview.id)}
									disabled={submittingAction}
								>
									<AdminIcon n="check" s={14} />
									Approuver
								</AdminButton>
							)}
							{activeReview.status !== "REJECTED" && (
								<AdminButton
									variant="ghost"
									onClick={() => setRejectingId(activeReview.id)}
									disabled={submittingAction}
								>
									<AdminIcon n="close" s={14} />
									Rejeter
								</AdminButton>
							)}
							<div style={{ marginLeft: "auto" }}>
								<AdminButton
									variant="ghost"
									onClick={() => setDeletingReview(activeReview)}
									disabled={submittingAction}
									style={{ color: "var(--a-danger)" }}
								>
									<AdminIcon n="trash" s={14} />
									Supprimer
								</AdminButton>
							</div>
						</div>
					</div>
				</>
			)}

			{/* MODAL PROMPT REJET AVEC ANIMATION */}
			{rejectingId && (
				<div
					className="animate-in fade-in duration-150"
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(20, 19, 15, 0.45)",
						backdropFilter: "blur(4px)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 9999,
					}}
					onClick={() => {
						setRejectingId(null);
						setRejectionReason("");
					}}
				>
					<div
						className="card animate-in zoom-in-95 duration-150"
						onClick={(e) => e.stopPropagation()}
						style={{
							width: 440,
							padding: 24,
							borderRadius: 14,
							background: "var(--a-surface)",
							boxShadow: "0 12px 32px -8px rgba(20, 19, 15, 0.2)",
						}}
					>
						<h3
							style={{
								margin: "0 0 6px",
								fontSize: 16,
								fontWeight: 700,
								color: "var(--a-ink)",
							}}
						>
							Rejeter l'avis client
						</h3>
						<p className="muted" style={{ margin: "0 0 16px", fontSize: 12.5, lineHeight: 1.45 }}>
							Indiquez la raison du rejet (optionnel). L'avis sera masqué de la boutique.
						</p>
						<label htmlFor="rejection-reason-input" style={{ marginBottom: 6, fontWeight: 600, fontSize: 12, display: "block", color: "var(--a-ink-soft)" }}>
							Motif du rejet
						</label>
						<textarea
							id="rejection-reason-input"
							className="inp focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
							rows={3}
							placeholder="Ex: Propos inappropriés, spam, informations erronées..."
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							style={{ marginBottom: 18, width: "100%", borderRadius: 6 }}
						/>
						<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
							<AdminButton
								variant="ghost"
								onClick={() => {
									setRejectingId(null);
									setRejectionReason("");
								}}
							>
								Annuler
							</AdminButton>
							<AdminButton
								variant="secondary"
								disabled={submittingAction}
								onClick={handleConfirmReject}
							>
								Confirmer le rejet
							</AdminButton>
						</div>
					</div>
				</div>
			)}

			{/* MODAL SUPPRESSION AVEC ANIMATION */}
			{deletingReview && (
				<div
					className="animate-in fade-in duration-150"
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(20, 19, 15, 0.45)",
						backdropFilter: "blur(4px)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 9999,
					}}
					onClick={() => setDeletingReview(null)}
				>
					<div
						className="card animate-in zoom-in-95 duration-150"
						onClick={(e) => e.stopPropagation()}
						style={{
							width: 420,
							padding: 24,
							borderRadius: 14,
							background: "var(--a-surface)",
							boxShadow: "0 12px 32px -8px rgba(20, 19, 15, 0.2)",
						}}
					>
						<div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
							<div
								style={{
									width: 40,
									height: 40,
									borderRadius: "50%",
									background: "var(--a-danger-soft)",
									color: "var(--a-danger)",
									display: "grid",
									placeItems: "center",
									flexShrink: 0,
								}}
							>
								<AdminIcon n="alert" s={20} />
							</div>
							<div>
								<h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "var(--a-ink)" }}>
									Supprimer cet avis
								</h3>
								<p className="muted" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5 }}>
									Voulez-vous vraiment supprimer définitivement cet avis pour{" "}
									<strong>{deletingReview.product?.title || "ce produit"}</strong> ?
								</p>
							</div>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 8,
								marginTop: 20,
							}}
						>
							<AdminButton variant="ghost" onClick={() => setDeletingReview(null)}>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								style={{ background: "var(--a-danger)" }}
								disabled={submittingAction}
								onClick={handleConfirmDelete}
							>
								Supprimer
							</AdminButton>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
